package one.healthium.profile.ui.weight

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import loco.GENERAL_KG_LABEL
import loco.GENERAL_LB_LABEL
import one.healthium.base.utils.kgToLb
import one.healthium.base.utils.lbToKg
import one.healthium.ui.compose.components.AppPicker
import one.healthium.ui.compose.theme.gray_7_80
import one.healthium.ui.compose.theme.gray_8
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

@Composable
fun WeightPicker(
    currentWeight: Float,
    isMetric: Boolean,
    onWeightChanged: (Float) -> Unit,
) {
    val currentWeightAsCurrentUnit = if (isMetric) currentWeight else currentWeight.kgToLb()
    var integerPart by remember(currentWeightAsCurrentUnit) { mutableStateOf(currentWeightAsCurrentUnit.toInt()) }
    var floatingPart by remember(currentWeightAsCurrentUnit) { mutableStateOf(currentWeightAsCurrentUnit - integerPart) }

    var topDividerOffset by remember { mutableStateOf<Dp?>(null) }
    var bottomDividerOffset by remember { mutableStateOf<Dp?>(null) }

    fun invokeCallback() {
        val updatedValue = integerPart + floatingPart
        val updatedValueInKg = if (isMetric) {
            updatedValue
        } else {
            updatedValue.lbToKg()
        }
        onWeightChanged(updatedValueInKg)
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(24.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.Center,
        ) {
            AppPicker(
                modifier = Modifier.weight(1f),
                scrollModifier = Modifier.testTag("weight_picker_integer"),
                items = remember { (1..999).map { "$it" } },
                startIndex = integerPart - 1,
                visibleItemsCount = 5,
                textModifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 12.dp, vertical = 4.dp),
                textAlign = TextAlign.End,
                onDrawTopDivider = { topDividerOffset = it },
                onDrawBottomDivider = { bottomDividerOffset = it },
                onSelectedItemChanged = { selectedItem ->
                    val newIntegerPart = selectedItem.toIntOrNull()
                    if (newIntegerPart != null) {
                        integerPart = newIntegerPart
                        invokeCallback()
                    }
                }
            )
            AppPicker(
                modifier = Modifier.weight(1f),
                scrollModifier = Modifier.testTag("weight_picker_decimal"),
                startIndex = floatingPart.times(10).roundToInt(),
                items = remember { (0..9).map { ",$it" } },
                visibleItemsCount = 5,
                textModifier = Modifier
                    .padding(end = 12.dp)
                    .padding(horizontal = 12.dp, vertical = 4.dp),
                textAlign = TextAlign.Start,
                trailing = {
                    Text(
                        modifier = Modifier.weight(1f),
                        text = if (isMetric) {
                            stringResource(loco.Res.string.GENERAL_KG_LABEL).lowercase()
                        } else {
                            stringResource(loco.Res.string.GENERAL_LB_LABEL).lowercase()
                        },
                        color = gray_8,
                    )
                },
                onSelectedItemChanged = { selectedItem ->
                    val newFloatingPart = selectedItem.substring(1).toIntOrNull()
                    if (newFloatingPart != null) {
                        floatingPart = newFloatingPart / 10f
                        invokeCallback()
                    }
                }
            )
        }

        if (topDividerOffset != null) {
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth().offset(y = topDividerOffset!!),
                color = gray_7_80,
            )
        }
        bottomDividerOffset?.let {
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth().offset(y = it),
                color = gray_7_80,
            )
        }
    }
}
